// Cabinet-class registry.
//
// Sources, in priority order:
//   1. ddb `cabinet_classes` collection (designer-editable, runtime).
//   2. Embedded `SYSTEM_CLASSES` from ./seed (fallback when ddb is
//      empty — first deploy of new bundle against old ddb, offline use).
//
// Public surface preserves the legacy `getCabinetClass(id)` /
// `CABINETS` / `CABINET_REGISTRY` shapes so downstream consumers
// (CabinetPalette, ProjectEditorPage, bom.ts, validation.ts, exporters)
// don't need to change to fetch the data.
//
// The store is a tiny Zustand instance because both React components
// (need reactivity, hooks) and pure helpers (need synchronous reads)
// call into it.

import { useEffect } from "react";
import { create } from "zustand";

import type { ParametricCabinetClass } from "@/lib/types";
import { useCollection } from "@/lib/ddb-hooks";
import type { CabinetClass } from "..";
import { fromParametric } from "./adapter";
import { SYSTEM_CLASSES } from "./seed";

interface RegistryState {
  /** Resolved CabinetClass wrappers keyed by class id. */
  classes: Record<string, CabinetClass>;
  /** The originating ParametricCabinetClass for each id — used by the
   *  class editor and serializer. */
  source: Record<string, ParametricCabinetClass>;
  upsert: (klass: ParametricCabinetClass) => void;
  upsertMany: (classes: ParametricCabinetClass[]) => void;
  remove: (id: string) => void;
}

const initialClasses: Record<string, CabinetClass> = Object.fromEntries(
  SYSTEM_CLASSES.map((k) => [k.id, fromParametric(k)]),
);
const initialSource: Record<string, ParametricCabinetClass> = Object.fromEntries(
  SYSTEM_CLASSES.map((k) => [k.id, k]),
);

export const useCabinetClassesStore = create<RegistryState>((set) => ({
  classes: { ...initialClasses },
  source: { ...initialSource },
  upsert: (klass) =>
    set((s) => ({
      classes: { ...s.classes, [klass.id]: fromParametric(klass) },
      source:  { ...s.source,  [klass.id]: klass },
    })),
  upsertMany: (ks) =>
    set((s) => {
      if (!ks.length) return s;
      const classes = { ...s.classes };
      const source  = { ...s.source };
      for (const k of ks) {
        // Embedded seed wins when its schemaVersion is higher than the
        // incoming ddb row — designers might be running against a stale
        // copy from a previous bundle. Equal or higher version → ddb
        // is authoritative (designer edits override embedded defaults).
        const existing = source[k.id];
        if (existing && (existing.schemaVersion ?? 0) > (k.schemaVersion ?? 0)) continue;
        classes[k.id] = fromParametric(k);
        source[k.id]  = k;
      }
      return { classes, source };
    }),
  remove: (id) =>
    set((s) => {
      const classes = { ...s.classes };
      const source  = { ...s.source };
      delete classes[id];
      delete source[id];
      return { classes, source };
    }),
}));

// ── Synchronous accessors (callable from pure helpers) ──────────

export function getCabinetClass(id: string): CabinetClass | null {
  return useCabinetClassesStore.getState().classes[id] ?? null;
}

export function getCabinetClasses(): CabinetClass[] {
  return Object.values(useCabinetClassesStore.getState().classes);
}

export function getParametricClass(id: string): ParametricCabinetClass | null {
  return useCabinetClassesStore.getState().source[id] ?? null;
}

// ── React hook: hydrate from ddb + return live list ─────────────

/** Subscribes to the `cabinet_classes` collection, merges any rows on
 *  top of the embedded seed, and returns the resulting live list. Use
 *  this in components that render a class picker (CabinetPalette) or
 *  the class editor list. */
export function useCabinetClasses(): {
  classes: CabinetClass[];
  source: Record<string, ParametricCabinetClass>;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useCollection<ParametricCabinetClass>(
    "cabinet_classes",
  );
  const classes = useCabinetClassesStore((s) => s.classes);
  const source  = useCabinetClassesStore((s) => s.source);
  const upsertMany = useCabinetClassesStore((s) => s.upsertMany);

  useEffect(() => {
    if (data && data.length) upsertMany(data);
  }, [data, upsertMany]);

  return {
    classes: Object.values(classes),
    source,
    isLoading,
    error,
  };
}

/** Boot-time hook: idempotently hydrates the registry from ddb. Mount
 *  once near the app root (App.tsx). Components that don't need to
 *  re-render on class changes can skip this and just call
 *  getCabinetClass synchronously. */
export function useHydrateCabinetClasses() {
  const { data } = useCollection<ParametricCabinetClass>("cabinet_classes");
  const upsertMany = useCabinetClassesStore((s) => s.upsertMany);
  useEffect(() => {
    if (data && data.length) upsertMany(data);
  }, [data, upsertMany]);
}
